import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Check } from "lucide-react";
import { Screen, TopBar, Logo, ScrollArea, BottomBar, PrimaryButton } from "@/components/jk/ui";
import { Avatar, Switch, FullScreenLoader } from "@/components/jk/kit";
import { TextArea } from "@/components/jk/ui";
import api from "@/lib/api";

const ReviewRequest = () => {
  const { id } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [firstNameOnly, setFirstNameOnly] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/review/${id}`).then(({ data }) => { setCtx(data); if (data.already_reviewed) setDone(true); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <FullScreenLoader />;

  const submit = async () => {
    if (rating === 0) return;
    setSubmitting(true);
    try {
      await api.post("/public/reviews", { booking_id: id, rating, text: text.trim(), show_first_name_only: firstNameOnly });
      setDone(true);
    } catch { setSubmitting(false); }
  };

  if (done) return (
    <Screen testid="review-done-screen">
      <ScrollArea className="flex flex-col items-center justify-center px-8 text-center">
        <div className="mt-40 flex h-18 w-18 items-center justify-center rounded-full bg-[#111111] p-5"><Check size={34} strokeWidth={2.5} className="text-white" /></div>
        <h1 className="mt-6 text-[26px] font-bold tracking-[-0.02em] text-[#111111]">Thank you!</h1>
        <p className="mt-2 text-[14px] text-[#666666]">Your review helps others find a trusted astrologer.</p>
      </ScrollArea>
    </Screen>
  );

  return (
    <Screen testid="review-screen">
      <TopBar center={<Logo className="h-[16px] w-auto" />} />
      <ScrollArea className="px-6 pt-6">
        <div className="flex flex-col items-center text-center">
          <Avatar name={ctx?.astrologer?.full_name} photo={ctx?.astrologer?.photo_url} size={72} />
          <h1 className="mt-4 text-[24px] font-bold leading-tight tracking-[-0.02em] text-[#111111]">How was your consultation?</h1>
          <p className="mt-2 text-[14px] text-[#666666]">with {ctx?.astrologer?.full_name}</p>
        </div>

        <div className="mt-8 flex justify-center gap-3" data-testid="star-rating">
          {[1, 2, 3, 4, 5].map((i) => (
            <button key={i} data-testid={`star-${i}`} onClick={() => setRating(i)} className="active:scale-90">
              <Star size={38} strokeWidth={1.5} className={i <= rating ? "fill-[#FF4D00] text-[#FF4D00]" : "text-[#d8d4ce]"} />
            </button>
          ))}
        </div>

        <div className="mt-8">
          <TextArea testid="review-text" rows={4} maxLength={500} placeholder="Share a few words about your experience (optional)" value={text} onChange={(e) => setText(e.target.value)} />
        </div>

        <div className="mt-4 flex items-center justify-between rounded-[14px] border border-[#e9e6e1] bg-white p-4">
          <div><p className="text-[14px] font-medium text-[#111111]">Show first name only</p><p className="text-[12px] text-[#666666]">Protect your privacy</p></div>
          <Switch testid="firstname-toggle" on={firstNameOnly} onChange={setFirstNameOnly} />
        </div>
      </ScrollArea>

      <BottomBar>
        <PrimaryButton testid="submit-review" onClick={submit} disabled={rating === 0 || submitting}>
          {submitting ? "Submitting…" : "Submit review"}
        </PrimaryButton>
      </BottomBar>
    </Screen>
  );
};

export default ReviewRequest;
